import type { PortalCatalogFilterFieldDto } from '@supply-mover-2-0/shared';
import { facetParamName } from './catalogFacetFilters';

/**
 * Open/closed rules for the catalog filter rail's headed sections (terms,
 * range and date filters). Every headed section starts collapsed so a long
 * facet such as Manufacturer does not push the rest of the rail off screen
 * (PD-16543), except one that is already narrowing the results.
 */

type FacetSectionFieldModel = Pick<
	PortalCatalogFilterFieldDto,
	'key' | 'kind' | 'resolution'
>;

function hasValue(value: string | null): boolean {
	return value != null && value.trim().length > 0;
}

/**
 * How many filters the shopper has set in a headed section, read from the
 * same URL params the section's controls write. A terms section counts each
 * distinct selected value; a range or date section counts as one filter when
 * either bound is set. Boolean and dynamic toggles render as a single
 * checkbox row with no header, so they never count.
 */
export function countActiveFacetSelections(
	field: FacetSectionFieldModel,
	searchParams: URLSearchParams,
): number {
	if (field.resolution === 'dynamic') {
		return 0;
	}

	switch (field.kind) {
		case 'terms':
			return new Set(
				searchParams
					.getAll(facetParamName(field))
					.filter((value) => hasValue(value)),
			).size;
		case 'range':
			return hasValue(searchParams.get(facetParamName(field, 'min'))) ||
				hasValue(searchParams.get(facetParamName(field, 'max')))
				? 1
				: 0;
		case 'date':
			return hasValue(searchParams.get(facetParamName(field, 'from'))) ||
				hasValue(searchParams.get(facetParamName(field, 'to')))
				? 1
				: 0;
		case 'boolean':
			return 0;
	}
}

/**
 * Whether a headed section is open. The shopper's own toggle always wins;
 * until they touch the section it is open only while it holds an active
 * filter, so a shared link or a Back navigation still shows what is
 * narrowing the results.
 */
export function isFacetSectionOpen(
	shopperToggle: boolean | undefined,
	activeSelectionCount: number,
): boolean {
	return shopperToggle ?? activeSelectionCount > 0;
}
